import { type Permission } from "@/lib/account/permission"
import { isClientError, type ErrorType } from "@/lib/clients"

import type {
  AuthenticationResponse,
  ConflictResponse,
  ErrorResponse,
  ForbiddenResponse,
  TooManyRequestsResponse,
} from "@boundless/bl-api-client/allauth/models"

export type AllauthErrorType = ErrorType<
  | ErrorResponse
  | AuthenticationResponse
  | ForbiddenResponse
  | ConflictResponse
  | TooManyRequestsResponse
>

export function isAccountPendingEmailVerificationError(
  error: AllauthErrorType,
): error is ErrorType<ErrorResponse> {
  if (
    error.response?.data.status === 401 &&
    error.response.data.data.flows.find((el) => {
      return el.id === "verify_email" && el.is_pending === true
    })
  ) {
    return true
  }

  return false
}

/**
 * @summary Check if the response indicates a login code was sent.
 *
 * `code/request` is an authentication-flow endpoint: on success allauth
 * returns a 401 with the `login_by_code` flow pending (the code has been
 * emailed and confirmation is the next step), not a 2xx.
 */
export function isLoginCodePendingResponse(error: AllauthErrorType): boolean {
  return (
    error.response?.data.status === 401 &&
    !!error.response.data.data.flows.find(
      (el) => el.id === "login_by_code" && el.is_pending === true,
    )
  )
}

/**
 * @summary Check if the response is due to the code-request throttle.
 *
 * The two code-request endpoints signal throttling differently:
 * `email/verify/resend` (and phone) return a plain 429. `code/request`
 * (login-by-code) instead fails form validation with a 400 and a
 * `too_many_login_attempts` error, since it's rate-limited inside
 * `RequestLoginCodeForm.clean_email` rather than via a 429 response.
 * Either way, it means the user needs to wait, not that sending failed.
 */
export function isCodeRequestRateLimitedResponse(
  error: AllauthErrorType,
): boolean {
  const data = error.response?.data
  if (data?.status === 429) {
    return true
  }
  return (
    data?.status === 400 &&
    !!data.errors?.some((el) => el.code === "too_many_login_attempts")
  )
}

export function getCodeRequestRateLimitedMessage(
  resendSeconds: number,
): string {
  return `The code was requested less than ${resendSeconds} seconds ago. Please wait a bit before requesting another.`
}

export function isAllauthError(
  error: unknown,
): error is ErrorType<ErrorResponse | AuthenticationResponse> {
  return (
    isClientError(error) &&
    typeof error.response === "object" &&
    error.response !== null
  )
}

export function isEmailAddressTakenError(
  error: unknown,
): error is ErrorType<ErrorResponse> {
  return (
    isAllauthError(error) &&
    error.response?.data.status === 400 &&
    !!error.response.data.errors?.some((item) => item.code === "email_taken")
  )
}

/**
 * @summary Check if an Embed signup was rejected because the Partner's User ID
 * already has an account, so the user should sign in instead.
 */
export function isEmbedAccountExistsError(
  error: unknown,
): error is ErrorType<ErrorResponse> {
  return (
    isAllauthError(error) &&
    error.response?.data.status === 400 &&
    !!error.response.data.errors?.some(
      (item) => item.code === "embed_account_exists",
    )
  )
}

/**
 * @summary Check if a 401 means an auth action completed, awaiting login.
 *
 * After a successful email verification or password reset (with
 * ACCOUNT_LOGIN_ON_PASSWORD_RESET off), allauth returns a 401 with no pending
 * flow - just the entry points to authenticate (login, signup, and any
 * enabled method such as login_by_code). We key off "nothing pending + login
 * and signup offered" rather than an exact flow count, since enabling extra
 * login methods changes how many flows are listed.
 */
function isCompletedAuthFlowError(
  error: unknown,
): error is ErrorType<AuthenticationResponse> {
  if (!isAllauthError(error) || error.response?.data.status !== 401) {
    return false
  }
  const flows = error.response.data.data.flows
  return (
    !flows.some((el) => el.is_pending) &&
    flows.some((el) => el.id === "login") &&
    flows.some((el) => el.id === "signup")
  )
}

/**
 * @summary Check if a 401 is actually a successful email verification.
 *
 * Allauth returns a 401 without the `verify_email` flow as the response to a
 * successful email key verification: verified, but not logged in yet.
 */
export function isEmailVerificationOkError(
  error: unknown,
): error is ErrorType<AuthenticationResponse> {
  return isCompletedAuthFlowError(error)
}

/**
 * @summary Check if a 401 error is actually a successful password reset.
 *
 * When ACCOUNT_LOGIN_ON_PASSWORD_RESET is False, allauth returns a 401 after a
 * successful password reset to indicate the user needs to log in manually.
 */
export function isPasswordResetOkError(
  error: unknown,
): error is ErrorType<AuthenticationResponse> {
  return isCompletedAuthFlowError(error)
}

/**
 * Pure permission check: returns whether every `required` permission is present
 * in `granted`.
 *
 * This is the single source of truth shared by the imperative store method
 * (used in router `beforeLoad` guards) and the reactive `useHasPermissions`
 * hook (used inside components).
 */
export function checkPermissions(
  granted: ReadonlyArray<Permission>,
  required: ReadonlyArray<Permission>,
): boolean {
  return required.every((p) => granted.includes(p))
}
